import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { PageFull } from "@/types/page";
import type { UpsertPagePayload } from "@/services/pagesService";
import { cn } from "@/lib/utils";
import { ChevronsUpDown, FileSearch, Loader2 } from "lucide-react";

const PROTECTED_ROUTES = ["/", "/blog", "/blog/:slug", "/admin-login", "/admin-reset-password"];

type ComboboxPage = { id: string; route: string; name: string };

type FormValues = {
  route: string;
  name: string;
  is_published: boolean;
  title: string;
  description: string;
  slug: string;
  canonical_url: string;
  og_title: string;
  og_description: string;
  og_image_url: string;
  indexable: boolean;
  no_follow: boolean;
  structured_data_json: string;
};

interface PageEditorProps {
  selectedPage?: PageFull | null;
  routes: ComboboxPage[];
  onSelectPage: (id: string) => void;
  onSave: (payload: UpsertPagePayload) => Promise<void> | void;
  onCancel: () => void;
  saving?: boolean;
  loadingRoutes?: boolean;
}

const badgeVariants = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warning: "border-amber-200 bg-amber-50 text-amber-700",
  danger: "border-rose-200 bg-rose-50 text-rose-700",
};

const toSlug = (value: string) => {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
};

const getOrigin = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return "https://www.toctoc.com.br";
};

export function PageEditor({
  selectedPage,
  routes,
  onSelectPage,
  onSave,
  onCancel,
  saving,
  loadingRoutes,
}: PageEditorProps) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      route: "",
      name: "",
      is_published: true,
      title: "",
      description: "",
      slug: "",
      canonical_url: "",
      og_title: "",
      og_description: "",
      og_image_url: "",
      indexable: true,
      no_follow: false,
      structured_data_json: "",
    },
  });

  useEffect(() => {
    if (!selectedPage) {
      reset({
        route: "",
        name: "",
        is_published: true,
        title: "",
        description: "",
        slug: "",
        canonical_url: "",
        og_title: "",
        og_description: "",
        og_image_url: "",
        indexable: true,
        no_follow: false,
        structured_data_json: "",
      });
      setSlugTouched(false);
      return;
    }

    reset({
      route: selectedPage.route ?? "",
      name: selectedPage.name ?? "",
      is_published: Boolean(selectedPage.is_published),
      title: selectedPage.title ?? "",
      description: selectedPage.description ?? "",
      slug: selectedPage.seo_slug ?? toSlug(selectedPage.route ?? ""),
      canonical_url: selectedPage.canonical_url ?? "",
      og_title: selectedPage.og_title ?? "",
      og_description: selectedPage.og_description ?? "",
      og_image_url: selectedPage.og_image_url ?? "",
      indexable: selectedPage.indexable ?? true,
      no_follow: selectedPage.no_follow ?? false,
      structured_data_json: selectedPage.structured_data_json
        ? JSON.stringify(selectedPage.structured_data_json, null, 2)
        : "",
    });
    setSlugTouched(Boolean(selectedPage.seo_slug));
  }, [reset, selectedPage]);

  const routeValue = watch("route");
  const nameValue = watch("name");
  const titleValue = watch("title");
  const descriptionValue = watch("description");
  const slugValue = watch("slug");
  const canonicalValue = watch("canonical_url");
  const ogImage = watch("og_image_url");
  const indexableValue = watch("indexable");
  const noFollowValue = watch("no_follow");
  const isPublished = watch("is_published");
  const structuredJsonValue = watch("structured_data_json");

  useEffect(() => {
    if (slugTouched) return;
    const base = routeValue || nameValue;
    if (!base) return;
    const nextSlug = toSlug(base);
    if (nextSlug) {
      setValue("slug", nextSlug, { shouldValidate: false, shouldDirty: false });
    }
  }, [routeValue, nameValue, setValue, slugTouched]);

  const seoHints = useMemo(() => {
    const titleLength = titleValue?.length ?? 0;
    const descriptionLength = descriptionValue?.length ?? 0;
    const titleStatus =
      titleLength >= 45 && titleLength <= 70
        ? "success"
        : titleLength === 0
        ? "danger"
        : "warning";

    const descriptionStatus =
      descriptionLength >= 120 && descriptionLength <= 160
        ? "success"
        : descriptionLength === 0
        ? "danger"
        : "warning";

    const ogStatus = ogImage ? "success" : "warning";
    const indexStatus = indexableValue && !noFollowValue ? "success" : "danger";
    const canonicalStatus = canonicalValue ? "success" : "warning";

    return [
      {
        label: `Título ${titleLength}/70`,
        status: titleStatus as keyof typeof badgeVariants,
      },
      {
        label: `Descrição ${descriptionLength}/160`,
        status: descriptionStatus as keyof typeof badgeVariants,
      },
      { label: ogImage ? "OG image definida" : "OG image ausente", status: ogStatus as keyof typeof badgeVariants },
      {
        label: indexableValue && !noFollowValue ? "Indexável" : "Revisar robots",
        status: indexStatus as keyof typeof badgeVariants,
      },
      { label: canonicalValue ? "Canonical ok" : "Canonical ausente", status: canonicalStatus as keyof typeof badgeVariants },
    ];
  }, [titleValue, descriptionValue, ogImage, indexableValue, noFollowValue, canonicalValue]);

  const handlePreview = () => {
    setPreviewOpen(true);
  };

  const onSubmit = handleSubmit(async values => {
    let slug = values.slug?.trim();
    if (!slug) {
      const base = values.route || values.name;
      slug = toSlug(base ?? "");
    }

    slug = toSlug(slug);

    if (!slug) {
      setError("slug", { message: "Slug é obrigatório." });
      return;
    }

    let structuredData: Record<string, unknown> | null = null;
    if (values.structured_data_json?.trim()) {
      try {
        structuredData = JSON.parse(values.structured_data_json.trim());
      } catch (error) {
        setError("structured_data_json", { message: "JSON inválido. Verifique a estrutura." });
        return;
      }
    }

    const payload: UpsertPagePayload = {
      page: {
        id: selectedPage?.id,
        route: values.route.trim(),
        name: values.name.trim(),
        is_published: values.is_published,
      },
      seo: {
        title: values.title.trim() || null,
        description: values.description.trim() || null,
        slug,
        canonical_url: values.canonical_url.trim() || null,
        og_title: values.og_title.trim() || null,
        og_description: values.og_description.trim() || null,
        og_image_url: values.og_image_url.trim() || null,
        indexable: values.indexable,
        no_follow: values.no_follow,
        structured_data_json: structuredData,
      },
    };

    await onSave(payload);
    setSlugTouched(true);
  });

  const robotsContent = useMemo(() => {
    const directives = [] as string[];
    directives.push(indexableValue ? "index" : "noindex");
    directives.push(noFollowValue ? "nofollow" : "follow");
    return directives.join(", ");
  }, [indexableValue, noFollowValue]);

  const canonicalFallback = useMemo(() => {
    if (canonicalValue) return canonicalValue;
    const slug = slugValue || toSlug(routeValue || nameValue || "");
    if (!slug) return getOrigin();
    return `${getOrigin()}/${slug}`;
  }, [canonicalValue, slugValue, routeValue, nameValue]);

  const currentPreview = useMemo(() => {
    const lines = [
      `<title>${titleValue || selectedPage?.name || ""}</title>`,
      `<meta name="description" content="${descriptionValue || ""}" />`,
      `<link rel="canonical" href="${canonicalFallback}" />`,
      `<meta name="robots" content="${robotsContent}" />`,
      ogImage ? `<meta property="og:image" content="${ogImage}" />` : null,
      titleValue ? `<meta property="og:title" content="${titleValue}" />` : null,
      descriptionValue ? `<meta property="og:description" content="${descriptionValue}" />` : null,
      ogImage ? `<meta name="twitter:card" content="summary_large_image" />` : `<meta name="twitter:card" content="summary" />`,
    ].filter(Boolean);

    return lines.join("\n");
  }, [titleValue, descriptionValue, canonicalFallback, robotsContent, ogImage, selectedPage?.name]);

  const isProtected = selectedPage?.route ? PROTECTED_ROUTES.includes(selectedPage.route) : false;

  return (
    <Card className="rounded-2xl border border-slate-200 bg-white/70 text-slate-900 backdrop-blur">
      <CardHeader className="space-y-4 pb-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <CardTitle className="text-xl font-semibold text-slate-900 text-pretty">Editor de SEO</CardTitle>
            <CardDescription className="text-sm text-slate-500 text-pretty">
              Ajuste metadados, publicação e estrutura de cada rota do ecossistema Toc Toc.
            </CardDescription>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
            Selecionar página existente
          </Label>
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={popoverOpen}
                className="w-full justify-between rounded-xl border-slate-200 bg-white px-4 py-6 text-left text-sm font-medium text-slate-600"
              >
                <span className="flex flex-col text-left">
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-400">Rota selecionada</span>
                  <span className="text-base font-semibold text-slate-900">
                    {selectedPage?.route || "Selecione uma rota"}
                  </span>
                </span>
                {loadingRoutes ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronsUpDown className="h-4 w-4 text-slate-400" />}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="z-50 w-[--radix-popover-trigger-width] max-w-[360px] rounded-xl border border-slate-200 bg-white/90 p-0 shadow-xl">
              <Command className="rounded-xl">
                <CommandInput placeholder="Buscar rota..." className="h-11" />
                <CommandEmpty>Nenhuma rota encontrada.</CommandEmpty>
                <CommandGroup className="max-h-64 overflow-y-auto">
                  {routes.map(route => (
                    <CommandItem
                      key={route.id}
                      value={`${route.route} ${route.name}`}
                      onSelect={() => {
                        setPopoverOpen(false);
                        onSelectPage(route.id);
                      }}
                      className={cn(
                        "flex flex-col gap-1 rounded-lg px-3 py-2 text-left",
                        selectedPage?.id === route.id && "bg-slate-100"
                      )}
                    >
                      <span className="text-sm font-semibold text-slate-900">{route.name}</span>
                      <span className="text-xs text-slate-500">{route.route}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex flex-wrap gap-2">
          {seoHints.map(hint => (
            <Badge key={hint.label} variant="outline" className={cn("text-xs font-medium", badgeVariants[hint.status])}>
              {hint.label}
            </Badge>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        <form onSubmit={onSubmit} className="space-y-6 max-w-[900px]">
          <section className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Configurações básicas</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,720px)]">
              <div className="space-y-2">
                <Label htmlFor="route">Rota</Label>
                <Input
                  id="route"
                  placeholder="/sobre"
                  className="h-11 w-full rounded-xl border-slate-200"
                  disabled={isProtected}
                  {...register("route", { required: "Informe a rota" })}
                  onBlur={() => {
                    clearErrors("route");
                  }}
                />
                {errors.route && <p className="text-xs text-rose-500">{errors.route.message}</p>}
                {isProtected && (
                  <p className="text-xs text-slate-500">Esta rota é protegida e não pode ser alterada.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Nome interno</Label>
                <Input
                  id="name"
                  placeholder="Sobre"
                  className="h-11 w-full rounded-xl border-slate-200"
                  {...register("name", { required: "Informe o nome" })}
                  onBlur={() => {
                    clearErrors("name");
                  }}
                />
                {errors.name && <p className="text-xs text-rose-500">{errors.name.message}</p>}
              </div>

              <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white/60 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Publicado</p>
                    <p className="text-xs text-slate-500">Controla a visibilidade pública da página.</p>
                  </div>
                  <Switch
                    checked={isPublished}
                    onCheckedChange={value => setValue("is_published", value, { shouldDirty: true })}
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Metadados de SEO</h3>
              <p className="text-xs text-slate-500">
                Títulos e descrições afetam diretamente o desempenho orgânico. Utilize termos relevantes e mantenha dentro dos limites recomendados.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Produtora Toc Toc · Fotografia imobiliária"
                  className="h-11 w-full rounded-xl border-slate-200"
                  {...register("title")}
                />
                <p className="text-xs text-slate-500">{titleValue?.length ?? 0} de 70 caracteres recomendados.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={4}
                  className="w-full rounded-xl border-slate-200"
                  placeholder="Resuma em até 160 caracteres..."
                  {...register("description")}
                />
                <p className="text-xs text-slate-500">{descriptionValue?.length ?? 0} de 160 caracteres recomendados.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  placeholder="sobre"
                  className="h-11 w-full rounded-xl border-slate-200"
                  {...register("slug")}
                  onChange={event => {
                    setSlugTouched(true);
                    setValue("slug", event.target.value, { shouldDirty: true });
                  }}
                  onBlur={event => {
                    const next = toSlug(event.target.value);
                    setValue("slug", next, { shouldDirty: true });
                    if (!next) {
                      setError("slug", { message: "Slug é obrigatório." });
                    } else {
                      clearErrors("slug");
                    }
                  }}
                />
                {errors.slug && <p className="text-xs text-rose-500">{errors.slug.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="canonical_url">Canonical</Label>
                <Input
                  id="canonical_url"
                  placeholder="https://www.toctoc.com.br/sobre"
                  className="h-11 w-full rounded-xl border-slate-200"
                  {...register("canonical_url")}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="og_title">OG title</Label>
                  <Input
                    id="og_title"
                    placeholder="Conheça a Toc Toc"
                    className="h-11 w-full rounded-xl border-slate-200"
                    {...register("og_title")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="og_description">OG description</Label>
                  <Input
                    id="og_description"
                    placeholder="Produção audiovisual para o mercado imobiliário"
                    className="h-11 w-full rounded-xl border-slate-200"
                    {...register("og_description")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="og_image_url">OG image URL</Label>
                <Input
                  id="og_image_url"
                  placeholder="https://.../imagem-og.jpg"
                  className="h-11 w-full rounded-xl border-slate-200"
                  {...register("og_image_url")}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white/60 p-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Permitir indexação</p>
                    <p className="text-xs text-slate-500">Define se os buscadores podem indexar a página.</p>
                  </div>
                  <Switch
                    checked={indexableValue}
                    onCheckedChange={value => setValue("indexable", value, { shouldDirty: true })}
                  />
                </div>
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white/60 p-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">No follow</p>
                    <p className="text-xs text-slate-500">Controle o rastreamento de links externos.</p>
                  </div>
                  <Switch checked={noFollowValue} onCheckedChange={value => setValue("no_follow", value, { shouldDirty: true })} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="structured_data_json">JSON-LD</Label>
                <Textarea
                  id="structured_data_json"
                  rows={6}
                  placeholder='{"@context":"https://schema.org"}'
                  className="w-full rounded-xl border-slate-200 font-mono text-xs"
                  {...register("structured_data_json")}
                />
                {errors.structured_data_json && (
                  <p className="text-xs text-rose-500">{errors.structured_data_json.message}</p>
                )}
              </div>
            </div>
          </section>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
            <Button type="button" variant="ghost" className="w-full sm:w-auto" onClick={onCancel}>
              Voltar
            </Button>
            <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={handlePreview}>
              <FileSearch className="mr-2 h-4 w-4" /> Preview
            </Button>
            <Button type="submit" className="w-full sm:w-auto" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar alterações
            </Button>
          </div>
        </form>
      </CardContent>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle>Preview de metadados</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-slate-700">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">URL canônica</p>
              <p className="mt-1 break-words text-sm font-medium text-slate-900 [overflow-wrap:anywhere]">{canonicalFallback}</p>
            </div>
            <pre className="max-h-80 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-900/90 p-4 text-xs text-slate-100">
              {currentPreview}
            </pre>
            {structuredJsonValue && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">JSON-LD</p>
                <pre className="max-h-64 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-900/90 p-4 text-xs text-emerald-100">
{structuredJsonValue}
                </pre>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
